import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Layout, theme } from 'antd';
import Landing from './components/Landing';
import Login from './components/Login';
import { isAuthed, setupAuth } from './auth';
import Sidebar from './components/Sidebar';
import DatasetList from './components/DatasetList';
import DatasetCreate from './components/DatasetCreate';
import DatasetManager from './components/DatasetManager';
import PromptList from './components/PromptList';
import PromptEdit from './components/PromptEdit';
import ApiKeyList from './components/ApiKeyList';
import CaptioningTaskList from './components/CaptioningTaskList';
import CaptioningTaskCreate from './components/CaptioningTaskCreate';
import CaptioningTaskView from './components/CaptioningTaskView';
import ModerationSessionList from './components/ModerationSessionList';
import ModerationSessionCreate from './components/ModerationSessionCreate';
import ModerationSessionView from './components/ModerationSessionView';
import AutoModerationView from './components/AutoModerationView';
import AutoModerationList from './components/AutoModerationList';
import './App.css';

const { Content } = Layout;

// The admin platform: the entire existing app, served under the /admin basename
// (so all internal routes/links resolve to /admin/*), behind a login gate.
function AdminApp() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [authed, setAuthed] = useState(isAuthed());

  useEffect(() => {
    setupAuth(() => setAuthed(false)); // a 401 anywhere bounces back to login
  }, []);

  return (
    <Router basename="/admin">
      {authed ? (
        <Layout style={{ minHeight: '100vh' }}>
          <Sidebar collapsed={sidebarCollapsed} onCollapse={setSidebarCollapsed} />
          <Layout style={{ marginLeft: sidebarCollapsed ? 80 : 260, transition: 'margin-left 0.2s' }}>
            <Content className="main-content" style={{ marginLeft: 0 }}>
              <Routes>
                <Route path="/" element={<DatasetList />} />
                <Route path="/datasets" element={<DatasetList />} />
                <Route path="/datasets/create" element={<DatasetCreate />} />
                <Route path="/datasets/:id" element={<DatasetManager />} />
                <Route path="/prompts" element={<PromptList />} />
                <Route path="/prompts/:id" element={<PromptEdit />} />
                <Route path="/api-keys" element={<ApiKeyList />} />
                <Route path="/captioning" element={<CaptioningTaskList />} />
                <Route path="/captioning/create" element={<CaptioningTaskCreate />} />
                <Route path="/captioning/:id" element={<CaptioningTaskView />} />
                <Route path="/moderation" element={<ModerationSessionList />} />
                <Route path="/moderation/create" element={<ModerationSessionCreate />} />
                <Route path="/moderation/:id" element={<ModerationSessionView />} />
                <Route path="/auto-moderation" element={<AutoModerationList />} />
                <Route path="/auto-moderation/:id" element={<AutoModerationView />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
              </Routes>
            </Content>
          </Layout>
        </Layout>
      ) : (
        <Routes>
          <Route path="/login" element={<Login onSuccess={() => setAuthed(true)} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </Router>
  );
}

function App() {
  // Public landing at "/", the admin platform under "/admin/*". They're separate
  // routers, so moving between them is a full navigation (a link, not a SPA push).
  const isAdmin = window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#6366f1',
          borderRadius: 8,
        },
        algorithm: theme.defaultAlgorithm,
      }}
    >
      {isAdmin ? <AdminApp /> : <Landing />}
    </ConfigProvider>
  );
}

export default App;
